import * as React from 'react'
import { LedgerItem, LedgerItemColumnConfig, RecurringInvoiceColumnProperty, RecurringInvoiceColumns, LedgerItemProperty, ResourceRendererOptions, ResourceRendererResult, RecurringInvoiceColumnConfig, RecurringInvoiceRendererOptions } from '../../types'
import { ResourceTableHeader } from './ResourceTable'
import ResourceTableRow from './ResourceTableRow'
import ResourceTableRowData from './ResourceTableRowData'
import ResourceTableRowActions from './ResourceTableRowActions'
import { IActionListItem } from '../ActionList/ActionList'
import i18n from '../../translations'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import NumberFormatter from '../../utilities/NumberFormatter'
import moment from '../../utilities/Moment'
import showdown from '../../utilities/showdown'
import styled from 'styled-components'
import Switch from '../Form/Switch'
import Badge from '../Badge/Badge'
import RecurringScheduleHelper from '../../helpers/RecurringScheduleHelper'

const RecurringAutoSend = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

export const DEFAULT_RECURRING_INVOICE_COLUMNS: RecurringInvoiceColumnProperty[] = [
  'contact',
  'schedule',
  'next_occurence_on',
  'finish',
  'auto_send',
  'net_total',
  'amount'
]

const RecurringInvoiceColumnConfigs: Record<RecurringInvoiceColumnProperty, RecurringInvoiceColumnConfig> = {
  id: {
    header: () => ({ title: i18n.t('LedgerItem::id') }),
    render: ({ ledgerItem }) => ledgerItem.id || '-'
  },
  contact: {
    header: () => ({ title: i18n.t('LedgerItem::client') }),
    render: ({ ledgerItem }) => ledgerItem.contact?.name || '-'
  },
  project: {
    header: () => ({ title: i18n.t('LedgerItem::project') }),
    render: ({ ledgerItem }) => ledgerItem.project?.name || '-'
  },
  auto_send: {
    header: () => ({ title: i18n.t('LedgerItem::auto_send', { align: 'right' }) }),
    render: ({ ledgerItem, onAutoSendToggle }) => <RecurringAutoSend>
      <Switch
        name='recurring_auto_send'
        onClick={(e) => onAutoSendToggle(e, ledgerItem)}
        checked={ledgerItem.recurring_auto_send}
      />
    </RecurringAutoSend>
  },
  schedule: {
    header: () => ({ title: i18n.t('LedgerItem::schedule') }),
    render: ({ ledgerItem }) => <Badge
      text={RecurringScheduleHelper.recurrenceUnitLabel(ledgerItem.recurring_schedule.recurrence_unit)}
      type='grey'
    />
  },
  next_occurence_on: {
    header: () => ({ title: i18n.t('LedgerItem::Next invoice') }),
    render: ({ ledgerItem, setting }) => ledgerItem?.recurring_schedule?.next_occurrence_on ? moment(ledgerItem.recurring_schedule.next_occurrence_on).format(setting.date_format) : '-',
  },
  finish: {
    header: () => ({ title: i18n.t('LedgerItem::finish') }),
    render: ({ ledgerItem, setting }) => ledgerItem?.recurring_schedule?.end_date ? moment(ledgerItem.recurring_schedule.end_date).format(setting.date_format) : i18n.t('LedgerItemsTable::Never'),
  },
  subject: {
    header: () => ({ title: i18n.t('LedgerItem::subject') }),
    render: ({ ledgerItem }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(ledgerItem.subject || '-') }} />
  },
  currency: {
    header: () => ({ title: i18n.t('LedgerItem::currency') }),
    render: ({ ledgerItem }) => ledgerItem.currency || '-'
  },
  subtotal: {
    header: () => ({ title: i18n.t('LedgerItem::subtotal'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.subtotal),
  },
  discount: {
    header: () => ({ title: i18n.t('LedgerItem::discount'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.discount)
  },
  net_total: {
    header: () => ({ title: i18n.t('LedgerItem::net_total'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.net_total)
  },
  tax_total: {
    header: () => ({ title: i18n.t('LedgerItem::tax_total'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.tax_total)
  },
  amount: {
    header: () => ({ title: i18n.t('LedgerItem::amount'), align: 'right' }),
    render: ({ ledgerItem, setting }) => NumberFormatter.formatCurrency(ledgerItem.currency || setting.default_currency, setting.number_format, ledgerItem.amount)
  },
  notes: {
    header: () => ({ title: i18n.t('LedgerItem::notes') }),
    render: ({ ledgerItem }) => <div dangerouslySetInnerHTML={{ __html: showdown.makeHtml(ledgerItem.notes || '-') }} />
  },
  created_at: {
    header: () => ({ title: i18n.t('LedgerItem::created_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.created_at ? moment(ledgerItem.created_at).format(setting.date_format) : '-'
  },
  updated_at: {
    header: () => ({ title: i18n.t('LedgerItem::updated_at') }),
    render: ({ ledgerItem, setting }) => ledgerItem.updated_at ? moment(ledgerItem.updated_at).format(setting.date_format) : '-'
  },
}

export function getRecurringInvoiceTableRenderer(options: RecurringInvoiceRendererOptions): ResourceRendererResult<LedgerItem, LedgerItemProperty> {
  const {
    onRowClick,
    selectedItems = [],
    onSelectionChange,
    onActionClick
  } = options

  const columns: { label: string, value: string }[] = RecurringInvoiceColumns.map(column => ({ label: i18n.t(`LedgerItem::${column}`), value: column }))
  const selectedColumns: LedgerItemProperty[] = LocalStorage
    .get(LocalStorageKey.RECURRING_INVOICE_COLUMNS, DEFAULT_RECURRING_INVOICE_COLUMNS)
    .filter((column: RecurringInvoiceColumnProperty) => RecurringInvoiceColumnConfigs[column])

  const headers: ResourceTableHeader[] =
    [
      ...selectedColumns
        .map(column => ({
          ...RecurringInvoiceColumnConfigs[column].header(),
          visible: true
        })),
      {
        title: '',
        stickyRight: '0px'
      }
    ]

  const renderRow = (ledgerItem: LedgerItem, _index: number): React.ReactElement => {
    const isSelected = selectedItems.includes(ledgerItem.id)
    let actions: IActionListItem[] = [
      { key: 'view', icon: 'eye', content: i18n.t('LedgerItemsTable::View') },
      { key: 'edit', icon: 'edit', content: i18n.t('LedgerItemsTable::Edit') },
      { key: 'delete', icon: 'trash-alt-solid', content: i18n.t('LedgerItemsTable::Delete'), destructive: true },
    ]

    const applyMaxWidthColumns: LedgerItemProperty[] = [
      'contact',
      'project',
      'number',
      'subject',
      'notes'
    ]

    let cells: React.ReactNode[] =
      [
        ...selectedColumns.map((column) => (
          <ResourceTableRowData
            key={`${ledgerItem.id}-${column}`}
            onClick={onRowClick ? () => onRowClick(ledgerItem) : undefined}
            maxWidth={applyMaxWidthColumns.includes(column) ? '300px' : undefined}
            ellipse={applyMaxWidthColumns.includes(column)}
            textAlign={RecurringInvoiceColumnConfigs[column].header().align}
          >
            {RecurringInvoiceColumnConfigs[column].render({ ledgerItem, ...options })}
          </ResourceTableRowData>
        ))
      ]


    // Add actions cell if actions are provided
    if (actions?.length > 0 && onActionClick) {
      cells = [
        ...cells,
        <ResourceTableRowActions
          key={`${ledgerItem.id}-actions`}
          actions={actions}
          onActionClick={(key) => onActionClick(key, ledgerItem)}
          sticky={true}
          stickyRight='0px'
        />
      ]
    }

    return (
      <ResourceTableRow
        key={ledgerItem.id}
        selected={isSelected}
        onSelectionChange={onSelectionChange ? (selected) => onSelectionChange(selected, ledgerItem.id) : undefined}
      >
        {cells}
      </ResourceTableRow>
    )
  }

  return {
    headers: headers,
    columns: columns,
    selectedColumns: selectedColumns,
    renderRow: renderRow,
  }
}

export default getRecurringInvoiceTableRenderer
